import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { Cart, CartItem, Product } from '../types';
import { ApiService } from '../services/api';
import { useAuth } from './useAuth';

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  addToCart: (product: Product, quantity: number) => Promise<boolean>;
  updateCartItem: (productId: number, quantity: number) => Promise<boolean>;
  removeFromCart: (productId: number) => Promise<boolean>;
  clearCart: () => Promise<boolean>;
  refreshCart: () => Promise<void>;
  getCartItemCount: () => number;
  getTotalPoints: () => { cashPoints: number; goldPoints: number; };
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // 사용자 인증 상태 변경 시 장바구니 로드
  useEffect(() => {
    if (isAuthenticated) {
      refreshCart();
    } else {
      setCart(null);
    }
  }, [isAuthenticated]);

  const refreshCart = async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      const response = await ApiService.getCart();
      
      if (response.success && response.data) {
        setCart(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = async (product: Product, quantity: number): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      setIsLoading(true);
      const response = await ApiService.addToCart(product.id, quantity);
      
      if (response.success) {
        await refreshCart();
        return true;
      } else {
        console.error('Failed to add to cart:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCartItem = async (productId: number, quantity: number): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      setIsLoading(true);
      const response = await ApiService.updateCartItem(productId, quantity);
      
      if (response.success) {
        await refreshCart();
        return true;
      } else {
        console.error('Failed to update cart item:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Update cart item error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (productId: number): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      setIsLoading(true);
      const response = await ApiService.removeFromCart(productId);
      
      if (response.success) {
        await refreshCart();
        return true;
      } else {
        console.error('Failed to remove from cart:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Remove from cart error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async (): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      setIsLoading(true);
      const response = await ApiService.clearCart();
      
      if (response.success) {
        setCart(null);
        return true;
      } else {
        console.error('Failed to clear cart:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Clear cart error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getCartItemCount = (): number => {
    return cart?.totalItems || 0;
  };

  const getTotalPoints = (): { cashPoints: number; goldPoints: number; } => {
    return {
      cashPoints: cart?.finalCashPoints || 0,
      goldPoints: cart?.finalGoldPoints || 0,
    };
  };

  const value: CartContextType = {
    cart,
    isLoading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart,
    getCartItemCount,
    getTotalPoints,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};